// Keeps phone screens awake in a connected room. iOS Safari blocks the Wake Lock
// API, so a hidden looping video is the fallback.

import { useState, useEffect, useRef, useCallback } from 'react';
import { createLogger } from '@airhead-games/shared/utilities';
import { useRoute } from './useRoute';
import { useRoomSocket } from './useRoomSocket';
import { useVisibilityRecovery } from './useSystemVisibilityRecovery';
import { ConnectionState } from '@airhead-games/shared/types';

const logger = createLogger('useSystemWakeLock');
const isSupported = typeof window !== 'undefined' && 'wakeLock' in navigator;

export const useWakeLock = () => {
  const { roomCode } = useRoute();
  const { connectionState } = useRoomSocket();

  // Wake lock should be active when in a room-coded route with an active connection
  const shouldLockBeActive =
    Boolean(roomCode) && connectionState === ConnectionState.CONNECTED;

  const [isWakeLockActive, setIsWakeLockActive] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const fakeVideoRef = useRef<HTMLVideoElement | null>(null);
  const isProgrammaticRelease = useRef(false);
  const shouldBeLockedRef = useRef(false);

  // Centralized error handler
  const handleWakeLockError = useCallback(
    (operation: string, error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error(`Failed to ${operation} wake lock: ${err.message}`);
      wakeLockRef.current = null;
      setIsWakeLockActive(false);
    },
    []
  );

  // Create fake video element for iOS fallback
  const createFakeVideo = useCallback(() => {
    try {
      const video = document.createElement('video');
      video.style.position = 'absolute';
      video.style.top = '-9999px';
      video.style.left = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;

      // Create a minimal video blob (1 frame of black)
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, 1, 1);

        const stream = canvas.captureStream(1); // 1 FPS
        video.srcObject = stream;

        // Add to DOM
        document.body.appendChild(video);

        logger.debug('Created fake video element for iOS wake lock fallback');
        return video;
      }
    } catch (error) {
      logger.error('Failed to create fake video element:', error);
    }
    return null;
  }, []);

  // Centralized cleanup
  const cleanupLock = useCallback(() => {
    wakeLockRef.current = null;

    // Clean up fake video
    if (fakeVideoRef.current) {
      try {
        fakeVideoRef.current.pause();
        fakeVideoRef.current.srcObject = null;
        document.body.removeChild(fakeVideoRef.current);
        fakeVideoRef.current = null;
        logger.debug('Cleaned up fake video element');
      } catch (error) {
        logger.error('Failed to clean up fake video element:', error);
      }
    }

    setIsWakeLockActive(false);
  }, []);

  const acquireLock = useCallback(async () => {
    if (wakeLockRef.current || fakeVideoRef.current) return; // Already active

    try {
      if (isSupported && navigator.wakeLock) {
        // Primary: Wake Lock API
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setIsWakeLockActive(true);
        logger.debug('Screen wake lock acquired while in active room');

        // Set up release listener
        wakeLockRef.current.addEventListener('release', () => {
          if (!isProgrammaticRelease.current) {
            logger.debug('Screen wake lock was released by the browser');
          }
          cleanupLock();
        });
      } else {
        // Fallback: Fake video for iOS and unsupported browsers
        fakeVideoRef.current = createFakeVideo();
        if (fakeVideoRef.current) {
          setIsWakeLockActive(true);
          logger.debug('Fake video wake lock fallback activated for iOS');

          // Start playing the video
          fakeVideoRef.current.play().catch((error) => {
            logger.error('Failed to play fake video for wake lock:', error);
            cleanupLock();
          });
        } else {
          logger.warn('Failed to create fake video fallback');
        }
      }
    } catch (error) {
      handleWakeLockError('acquire', error);

      // Try fake video fallback if Wake Lock API fails
      if (!fakeVideoRef.current) {
        logger.debug('Wake Lock API failed, attempting fake video fallback...');
        fakeVideoRef.current = createFakeVideo();
        if (fakeVideoRef.current) {
          setIsWakeLockActive(true);
          logger.debug(
            'Fake video wake lock fallback activated after Wake Lock failure'
          );

          fakeVideoRef.current.play().catch((fallbackError) => {
            logger.error('Failed to play fake video fallback:', fallbackError);
            cleanupLock();
          });
        }
      }
    }
  }, [handleWakeLockError, cleanupLock, createFakeVideo]);

  const releaseLock = useCallback(async () => {
    if (!wakeLockRef.current && !fakeVideoRef.current) return;

    try {
      isProgrammaticRelease.current = true;

      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        logger.debug('Screen wake lock released programmatically');
      }

      if (fakeVideoRef.current) {
        logger.debug('Fake video wake lock released programmatically');
      }

      cleanupLock();
    } catch (error) {
      handleWakeLockError('release', error);
    } finally {
      isProgrammaticRelease.current = false;
    }
  }, [handleWakeLockError, cleanupLock]);

  const reacquireWakeLock = useCallback(async () => {
    // Only reacquire if we should be locked but currently aren't
    if (
      shouldBeLockedRef.current &&
      !wakeLockRef.current &&
      !fakeVideoRef.current
    ) {
      logger.debug('Attempting to reacquire wake lock...');
      await acquireLock();
    }
  }, [acquireLock]);

  // Set up visibility recovery for wake lock reacquisition
  useVisibilityRecovery({
    onVisible: shouldLockBeActive ? reacquireWakeLock : undefined,
    onFocus: shouldLockBeActive ? reacquireWakeLock : undefined,
  });

  // Track what the lock state should be
  useEffect(() => {
    shouldBeLockedRef.current = shouldLockBeActive;
  }, [shouldLockBeActive]);

  useEffect(() => {
    if (shouldLockBeActive) {
      acquireLock();
    } else {
      releaseLock();
    }

    // The cleanup function will release the lock if the component unmounts
    // while the lock is active. This is a crucial safety measure.
    return () => {
      releaseLock();
    };
  }, [shouldLockBeActive, acquireLock, releaseLock]);

  return { isWakeLockActive, reacquireWakeLock };
};
